import _ from "lodash";
import i18n from "@/i18n.js";
import { APP_ROUTE } from "./route";
import * as Sentry from "@sentry/browser";
import CONSTANT from "@/utils/constant.js";

const slashes = require("slashes");

const removeWhiteSpace = text => text.replace(/\s/g, "");

export const DEFAULT_ERROR_MESSAGE = "Default Server Error";

const getErrorResponseMessage = (response, defaultMessage) => {
	let errorMessage = defaultMessage || DEFAULT_ERROR_MESSAGE;

	const message = _.get(response, "0.message");
	if (message) {
		errorMessage = message;
	}

	return errorMessage;
};

const getError = (action, option, xhr) => {
	let msg;
	if (xhr.response) {
		msg = `${xhr.response.error || xhr.response}`;
	} else if (xhr.responseText) {
		msg = `${xhr.responseText}`;
	} else {
		msg = `fail to post ${action} ${xhr.status}`;
	}

	const err = new Error(msg);
	err.status = xhr.status;
	err.method = "post";
	err.url = action;
	return err;
};

const getBody = xhr => {
	const text = xhr.responseText || xhr.response;
	if (!text) {
		return text;
	}

	try {
		return JSON.parse(text);
	} catch (e) {
		return text;
	}
};

const Numbers = new Array(" không", " một", " hai", " ba", " bốn", " năm", " sáu", " bảy", " tám", " chín");
const Suffix = new Array("", " nghìn", " triệu", " tỷ", " nghìn tỷ", " triệu tỷ");

export default {
	currentAppPath: () => {
		// eslint-disable-next-line no-constant-condition
		if (window.location.hostname === "localhost" || true) {
			return APP_ROUTE.MAIN_APP_ROUTE_PATH;
		}

		return "/";
	},

	toString: array => {
		return slashes.add(JSON.stringify(array));
	},

	slashes: string => {
		return slashes.add(string);
	},

	getErrorResponseMessage,

	notifyError: ({ context, error, options }) => {
		const title = context.$t(`Notify.${removeWhiteSpace("Error Title")}`);
		let message = context.$t(`Notify.${removeWhiteSpace(DEFAULT_ERROR_MESSAGE)}`);

		const errorResponse = error ? error.graphQLErrors || error.errors : null;
		message = getErrorResponseMessage(errorResponse, message);

		if (errorResponse) {
			// Sentry config
			Sentry.captureMessage("[Util notifyError]");
			Sentry.captureException(error);
		}

		return context.$vs.notify({
			title: title,
			text: message,
			iconPack: "feather",
			icon: "icon-alert-circle",
			color: "danger",
			time: 30000,
			...options
		});
	},

	notifySuccess: ({ context, options }) => {
		return context.$vs.notify({
			title: context.$t(`Notify.Notification`),
			text: context.$t(removeWhiteSpace(`Notify.Handle Successful`)),
			iconPack: "feather",
			icon: "icon-alert-circle",
			color: "success",
			time: 5000,
			...options
		});
	},

	notifyWarning: ({ context, options }) => {
		// context.$notify.clear();
		return context.$vs.notify({
			title: context.$t(`Notify.Notification`),
			iconPack: "feather",
			icon: "icon-alert-circle",
			color: "warning",
			time: 5000,
			...options
		});
	},

	numFormat(num, dec, isHasCurrency) {
		if (typeof num === "undefined") return "";
		// let preferences = user.getUserRefs();
		// let decimals = dec || 0;
		// let lang = i18n.locale;
		// let decimalPoint = lang === "vi" ? "," : ".";
		// let separator = lang === "vi" ? "." : ",";

		let decimals = dec || Number.isInteger(num) ? 0 : 1;
		let decimalPoint = ".";
		let separator = ",";
		const currency = i18n.locale === "vi" ? "VNĐ" : "";
		if (isHasCurrency) {
			return number_format(Number(num), decimals, decimalPoint, separator) + ` ${currency}`;
		}
		return number_format(Number(num), decimals, decimalPoint, separator);
	},

	upload(option) {
		if (typeof XMLHttpRequest === "undefined") {
			return;
		}

		const xhr = new XMLHttpRequest();
		const action = option.action;

		if (xhr.upload) {
			xhr.upload.onprogress = function progress(e) {
				if (e.total > 0) {
					e.percent = (e.loaded / e.total) * 100;
				}
				option.onProgress(e);
			};
		}

		const formData = new FormData();

		if (option.data) {
			Object.keys(option.data).forEach(key => {
				formData.append(key, option.data[key]);
			});
		}

		formData.append(option.filename, option.file, option.file.name);

		xhr.onerror = function error(e) {
			option.onError(e);
		};

		xhr.onload = function onload() {
			if (xhr.status < 200 || xhr.status >= 300) {
				return option.onError(getError(action, option, xhr));
			}

			option.onSuccess(getBody(xhr));
		};

		xhr.open("post", action, true);

		if (option.withCredentials && "withCredentials" in xhr) {
			xhr.withCredentials = true;
		}

		const headers = option.headers || {};

		for (let item in headers) {
			if (headers.hasOwnProperty(item) && headers[item] !== null) {
				xhr.setRequestHeader(item, headers[item]);
			}
		}
		xhr.send(formData);
		return xhr;
	},

	arrayMove(arr, oldIndex, newIndex) {
		const sortedArray = [...arr];
		if (newIndex >= arr.length) {
			var k = newIndex - arr.length + 1;
			while (k--) {
				sortedArray.push(undefined);
			}
		}
		sortedArray.splice(newIndex, 0, sortedArray.splice(oldIndex, 1)[0]);
		return sortedArray;
	},

	formatDynamicFormDataStructure(formData) {
		return {
			...formData,
			controls: formData.controls.map(item => {
				let calNumbers = {};
				if (
					item.control === CONSTANT.request.control.checkbox ||
					item.control === CONSTANT.request.control.attach ||
					item.control === CONSTANT.request.control.repeater ||
					item.control === CONSTANT.request.control.relation
				) {
					if (_.isEmpty(item.value)) {
						item.value = [];
					}
				}

				if (item.control === CONSTANT.request.control.number || item.control === CONSTANT.request.control.calculator) {
					calNumbers[item.id] = item.value;
				}

				return typeof item.value === "undefined" ? { ...item, value: "" } : item;
			})
		};
	},

	formatDynamicFormData(form, fieldDataKey = "data", formDataKey = "parent") {
		const formatFormData = this.formatDynamicFormDataStructure(form);
		const parsedFormData = formatFormData[fieldDataKey] ? JSON.parse(formatFormData[fieldDataKey]) : null;
		const formDataByKey = formDataKey ? _.get(parsedFormData, formDataKey) || {} : parsedFormData;
		return {
			...formatFormData,
			controls: !_.isEmpty(formDataByKey)
				? formatFormData.controls.map(field => ({
						...field,
						value:
							field.control !== CONSTANT.request.control.attach &&
							field.control !== CONSTANT.request.control.checkbox &&
							field.control !== CONSTANT.request.control.repeater &&
							field.control !== CONSTANT.request.control.relation
								? formDataByKey[field.id]
								: formDataByKey[field.id] && !_.isEmpty(formDataByKey[field.id])
								? formDataByKey[field.id]
								: []
				  })) // eslint-disable-line no-mixed-spaces-and-tabs
				: formatFormData.controls
		};
	},
	nonAccentVietnamese(str) {
		str = str.toLowerCase();
		str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
		str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
		str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
		str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
		str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
		str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
		str = str.replace(/đ/g, "d");
		// Some system encode vietnamese combining accent as individual utf-8 characters
		str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ""); // Huyền sắc hỏi ngã nặng
		str = str.replace(/\u02C6|\u0306|\u031B/g, ""); // Â, Ê, Ă, Ơ, Ư
		return str;
	},

	//1. Hàm đọc số có ba chữ số;
	readThreeNumber(numberInput) {
		let hundred;
		let dozen;
		let unit;
		let result = "";
		hundred = parseInt(numberInput / 100);
		dozen = parseInt((numberInput % 100) / 10);
		unit = numberInput % 10;
		if (hundred == 0 && dozen == 0 && unit == 0) return "";
		if (hundred != 0) {
			result += Numbers[hundred] + " trăm";
			if (dozen == 0 && unit != 0) result += " linh";
		}
		if (dozen != 0 && dozen != 1) {
			result += Numbers[dozen] + " mươi";
			if (dozen == 0 && unit != 0) result = result + " linh";
		}
		if (dozen == 1) result += " mười";
		switch (unit) {
			case 1:
				if (dozen != 0 && dozen != 1) {
					result += " mốt";
				} else {
					result += Numbers[unit];
				}
				break;
			case 5:
				if (dozen == 0) {
					result += Numbers[unit];
				} else {
					result += " lăm";
				}
				break;
			default:
				if (unit != 0) {
					result += Numbers[unit];
				}
				break;
		}
		return result;
	},

	//2. Hàm đọc số thành chữ (Sử dụng hàm đọc số có ba chữ số)

	numberToWords(_numberInput) {
		if (isNaN(_numberInput)) {
			return _numberInput;
		}
		let lan = 0;
		let i = 0;
		let _number = 0;
		let result = "";
		let tmp = "";
		let position = new Array();
		if (_numberInput == 0) return "Không";
		if (_numberInput > 0) {
			_number = _numberInput;
		} else {
			_number = -_numberInput;
		}
		if (_numberInput > 8999999999999999) {
			//_numberInput = 0;
			return "Overflow Number";
		}
		position[5] = Math.floor(_number / 1000000000000000);
		if (isNaN(position[5])) position[5] = "0";
		_number = _number - parseFloat(position[5].toString()) * 1000000000000000;
		position[4] = Math.floor(_number / 1000000000000);
		if (isNaN(position[4])) position[4] = "0";
		_number = _number - parseFloat(position[4].toString()) * 1000000000000;
		position[3] = Math.floor(_number / 1000000000);
		if (isNaN(position[3])) position[3] = "0";
		_number = _number - parseFloat(position[3].toString()) * 1000000000;
		position[2] = parseInt(_number / 1000000);
		if (isNaN(position[2])) position[2] = "0";
		position[1] = parseInt((_number % 1000000) / 1000);
		if (isNaN(position[1])) position[1] = "0";
		position[0] = parseInt(_number % 1000);
		if (isNaN(position[0])) position[0] = "0";
		if (position[5] > 0) {
			lan = 5;
		} else if (position[4] > 0) {
			lan = 4;
		} else if (position[3] > 0) {
			lan = 3;
		} else if (position[2] > 0) {
			lan = 2;
		} else if (position[1] > 0) {
			lan = 1;
		} else {
			lan = 0;
		}
		for (i = lan; i >= 0; i--) {
			tmp = this.readThreeNumber(position[i]);
			result += tmp;
			if (position[i] > 0) result += Suffix[i];
			if (i > 0 && tmp.length > 0) result += ""; //&& (!string.IsNullOrEmpty(tmp))
		}
		if (result.substring(result.length - 1) == "") {
			result = result.substring(0, result.length - 1);
		}
		if (_numberInput < 0) return "Âm" + result;
		result = result.substring(1, 2).toUpperCase() + result.substring(2);
		return result; //.substring(0, 1);//.toUpperCase();// + result.substring(1);
	}
};

export function number_format(number, decimals, decPoint, thousandsSep) {
	// eslint-disable-line camelcase
	//  discuss at: http://locutus.io/php/number_format/
	// original by: Jonas Raoni Soares Silva (http://www.jsfromhell.com)
	// improved by: Kevin van Zonneveld (http://kvz.io)
	// improved by: davook
	// improved by: Brett Zamir (http://brett-zamir.me)
	// improved by: Brett Zamir (http://brett-zamir.me)
	// improved by: Theriault (https://github.com/Theriault)
	// improved by: Kevin van Zonneveld (http://kvz.io)
	// bugfixed by: Michael White (http://getsprink.com)
	// bugfixed by: Benjamin Lupton
	// bugfixed by: Allan Jensen (http://www.winternet.no)
	// bugfixed by: Howard Yeend
	// bugfixed by: Diogo Resende
	// bugfixed by: Rival
	// bugfixed by: Brett Zamir (http://brett-zamir.me)
	//  revised by: Jonas Raoni Soares Silva (http://www.jsfromhell.com)
	//  revised by: Luke Smith (http://lucassmith.name)
	//    input by: Kheang Hok Chin (http://www.distantia.ca/)
	//    input by: Jay Klehr
	//    input by: Amir Habibi (http://www.residence-mixte.com/)
	//    input by: Amirouche
	//   example 1: number_format(1234.56)
	//   returns 1: '1,235'
	//   example 2: number_format(1234.56, 2, ',', ' ')
	//   returns 2: '1 234,56'
	//   example 3: number_format(1234.5678, 2, '.', '')
	//   returns 3: '1234.57'
	//   example 4: number_format(67, 2, ',', '.')
	//   returns 4: '67,00'
	//   example 5: number_format(1000)
	//   returns 5: '1,000'
	//   example 6: number_format(67.311, 2)
	//   returns 6: '67.31'
	//   example 7: number_format(1000.55, 1)
	//   returns 7: '1,000.6'
	//   example 8: number_format(67000, 5, ',', '.')
	//   returns 8: '67.000,00000'
	//   example 9: number_format(0.9, 0)
	//   returns 9: '1'
	//  example 10: number_format('1.20', 2)
	//  returns 10: '1.20'
	//  example 11: number_format('1.20', 4)
	//  returns 11: '1.2000'
	//  example 12: number_format('1.2000', 3)
	//  returns 12: '1.200'
	//  example 13: number_format('1 000,50', 2, '.', ' ')
	//  returns 13: '100 050.00'
	//  example 14: number_format(1e-8, 8, '.', '')
	//  returns 14: '0.00000001'

	number = (number + "").replace(/[^0-9+\-Ee.]/g, "");
	var n = !isFinite(+number) ? 0 : +number;
	var prec = !isFinite(+decimals) ? 0 : Math.abs(decimals);
	var sep = typeof thousandsSep === "undefined" ? "," : thousandsSep;
	var dec = typeof decPoint === "undefined" ? "." : decPoint;
	var s = "";

	var toFixedFix = function(n, prec) {
		if (("" + n).indexOf("e") === -1) {
			return +(Math.round(n + "e+" + prec) + "e-" + prec);
		} else {
			var arr = ("" + n).split("e");
			var sig = "";
			if (+arr[1] + prec > 0) {
				sig = "+";
			}
			return (+(Math.round(+arr[0] + "e" + sig + (+arr[1] + prec)) + "e-" + prec)).toFixed(prec);
		}
	};

	// @todo: for IE parseFloat(0.55).toFixed(0) = 0;
	s = (prec ? toFixedFix(n, prec).toString() : "" + Math.round(n)).split(".");
	if (s[0].length > 3) {
		s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
	}
	if ((s[1] || "").length < prec) {
		s[1] = s[1] || "";
		s[1] += new Array(prec - s[1].length + 1).join("0");
	}

	return s.join(dec);
}
